<template>
  <div :class="embedded ? '' : 'ledger-page pb-5 pb-md-2'">
    <b-card
      class="rounded-lg"
      :class="{ 'border-0 shadow-none': embedded }"
    >
      <h4
        v-if="!embedded"
        class="mb-50"
      >
        {{ $t('ledger_sub_statement') }}
      </h4>
      <div v-if="statement && statement.subcontractor && !embedded">
        {{ statement.subcontractor.name }}
      </div>
      <b-row class="mt-1">
        <b-col
          cols="6"
          md="3"
        >
          <b-form-input
            v-model="from"
            type="date"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-input
            v-model="to"
            type="date"
          />
        </b-col>
        <b-col
          cols="12"
          md="2"
        >
          <b-button
            variant="primary"
            class="rounded-pill"
            @click="load"
          >
            {{ $t('ledger_assets_load') }}
          </b-button>
        </b-col>
      </b-row>
      <div
        v-if="statement"
        class="ledger-sub-kpis mt-1"
      >
        <div><span class="text-muted">2115</span><strong class="ledger-num d-block">{{ money(statement.payable_balance) }}</strong></div>
        <div><span class="text-muted">2120</span><strong class="ledger-num d-block">{{ money(statement.retention_balance) }}</strong></div>
        <div><span class="text-muted">{{ $t('ledger_inv_opening') }}</span><strong class="ledger-num d-block">{{ money(statement.opening_balance) }}</strong></div>
        <div><span class="text-muted">{{ $t('ledger_inv_closing') }}</span><strong class="ledger-num d-block">{{ money(statement.closing_balance) }}</strong></div>
      </div>
    </b-card>
    <b-card
      v-if="statement"
      class="rounded-lg"
    >
      <div class="d-none d-md-block">
        <b-table
          :items="statement.movements || []"
          :fields="fields"
          small
          responsive
          class="ledger-dense-table"
        >
          <template #cell(debit)="data"><span class="ledger-num">{{ money(data.item.debit) }}</span></template>
          <template #cell(credit)="data"><span class="ledger-num">{{ money(data.item.credit) }}</span></template>
          <template #cell(balance)="data"><span class="ledger-num">{{ money(data.item.balance) }}</span></template>
        </b-table>
      </div>
      <div class="d-md-none">
        <article
          v-for="(row, index) in statement.movements || []"
          :key="index"
          class="ledger-sub-card"
        >
          <div>{{ row.date }} · {{ row.account_code }}</div>
          <div class="ledger-num">
            {{ money(row.balance) }}
          </div>
        </article>
      </div>
    </b-card>
    <div
      v-if="statement && !embedded"
      class="ledger-sub-sticky d-md-none"
    >
      <span>{{ $t('ledger_inv_closing') }}</span>
      <strong class="ledger-num">{{ money(statement.closing_balance) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSubcontractorStatement } from '@/api/accounting/subcontractors'
import { oneFrom, formatLedgerMoney, todayIso, apiErrorMessage } from '@/utils/ledger'

function monthStart() {
  const date = new Date()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${date.getFullYear()}-${m}-01`
}

export default {
  name: 'SubcontractorStatement',
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable,
  },
  props: {
    subcontractorId: { type: [Number, String], default: null },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return { from: monthStart(), to: todayIso(), statement: null }
  },
  computed: {
    id() {
      return this.subcontractorId || this.$route.params.id
    },
    fields() {
      return [
        { key: 'date', label: this.$t('ledger_date') },
        { key: 'entry_number', label: this.$t('ledger_entry_number') },
        { key: 'account_code', label: this.$t('ledger_code') },
        { key: 'description', label: this.$t('ledger_description') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
        { key: 'balance', label: this.$t('ledger_inv_running') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    async load() {
      try {
        const res = await getSubcontractorStatement(this.id, { from: this.from, to: this.to })
        this.statement = oneFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-sub-kpis { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
@media (min-width: 1280px) {
  .ledger-sub-kpis { grid-template-columns: repeat(4, 1fr); }
}
.ledger-sub-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
.ledger-sub-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
